// Generated by Copilot- Start
import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../../video-lab/dtv-text";
import CardMedia from "@mui/material/CardMedia";
import { MBL_SIZE, TBL_SIZE, colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { channelLogoSx, channelLogoChipFor, handleChannelLogoError } from "lib/helper/channel-logo-url";

interface ModalChannelHeaderProps {
  logo?: string | { mobile?: string; tablet?: string; default: string };
  label: string;
  logoAlt?: string;
  /** Logo size preset: 'default' (64x64) or 'large' (116x66) */
  logoSize?: "default" | "large";
  /** Custom logo styles */
  logoStyles?: Record<string, any>;
  /** Custom label styles */
  labelStyles?: Record<string, any>;
  /** Custom container gap */
  gap?: string | Record<string, string>;
  /**
   * WAT-11362 — the server's per-channel OV-logo decision, carried on the data. When true the
   * logo is an OV catalog wordmark: it needs the white chip + inset, and a 404 must fall back
   * to the no-logo asset instead of hiding this header (which also holds the channel name).
   */
  isOvLogo?: boolean;
}

/**
 * Reusable modal header component showing channel logo and label
 * Supports both simple string logos and responsive picture sources
 * Used across: ChannelModalV2, RsnChannelModalV2, LocalChannelModalV2, MyFreeDtvModalV2, SvodModalV2
 */
const ModalChannelHeader: React.FC<ModalChannelHeaderProps> = ({
  logo,
  label,
  logoAlt,
  logoSize = "default",
  logoStyles = {},
  labelStyles = {},
  gap = {
    xs: "16px",
    sm: "20px"
  },
  isOvLogo
}) => {
  try {
    // Determine if logo is responsive object or simple string
    const isResponsiveLogo = typeof logo === "object" && logo !== null;
    const logoSrc = isResponsiveLogo ? (logo as { mobile?: string; tablet?: string; default: string }).default : (logo as string);

    // Size presets
    const sizePresets = {
      default: {
        height: "64px",
        width: "64px"
      },
      large: {
        height: "66px",
        width: "116px"
      }
    };

    const logoSizeStyles = sizePresets[logoSize];

    // Base logo styles
    const baseLogoStyles = {
      display: "inline-flex",
      verticalAlign: "middle",
      objectFit: "contain" as const,
      position: "relative" as const,
      ...logoSizeStyles,
      ...logoStyles
    };

    // WAT-11362: this header is OV-fed (ChannelCardV2.logo carries the OV imageURL when the
    // flag is on). Two things had to change for that source:
    //   1. onError hid `parentElement` — which is #modal-channel-header, the box that also
    //      holds the channel NAME — so a 404'd OV url (live on News Mix / Heroes & Icons)
    //      erased the whole header. An OV logo now falls back to the no-logo asset instead.
    //      A non-OV logo still hides the parent, exactly as production does today.
    //   2. An OV wordmark is full-bleed and needs the white chip behind it. The chip carries
    //      transform/geometry, so it must sit on a definite BLOCK box, never on the img and
    //      never on an inline ancestor — hence the dedicated wrapper, rendered only when the
    //      flag is on so flag-off stays byte-identical.
    const channelLogoImg = (
      <CardMedia
        alt={logoAlt || label}
        component="img"
        loading="lazy"
        src={logoSrc}
        sx={{
          ...baseLogoStyles,
          ...channelLogoSx(isOvLogo, logoSrc)
        }}
        onError={e => handleChannelLogoError(e, isOvLogo, "hideParent")}
      />
    );

    const channelLogo = isOvLogo ? (
      <DtvBox sx={{
        display: "block",
        flex: "0 0 auto",
        ...logoSizeStyles,
        ...channelLogoChipFor(true, logoSrc)
      }}>
        {channelLogoImg}
      </DtvBox>
    ) : channelLogoImg;

    return (
      <DtvBox id="modal-channel-header" sx={{
 display: "flex",
width: "100%",
alignItems: "center",
gap
}}>
        {logo && (
          <>
            {isResponsiveLogo ? (
              // Responsive picture element for SVOD logos
              <picture>
                {(logo as { mobile?: string; tablet?: string; default: string }).mobile && (
                  <source media={MBL_SIZE} srcSet={(logo as { mobile?: string; tablet?: string; default: string }).mobile} />
                )}
                {(logo as { mobile?: string; tablet?: string; default: string }).tablet && (
                  <source media={TBL_SIZE} srcSet={(logo as { mobile?: string; tablet?: string; default: string }).tablet} />
                )}
                <img
                  alt={logoAlt || label}
                  src={(logo as { mobile?: string; tablet?: string; default: string }).default}
                  style={{
                    ...baseLogoStyles,
                    paddingTop: "1.3px",
                    fontSize: "8px",
                    fill: colorPalette?.neutral?.[600]
                  }}
                  onError={e => {
                    if (e.currentTarget.parentElement) {
                      e.currentTarget.parentElement.style.display = "none";
                    }
                  }}
                />
              </picture>
            ) : (
              // Simple CardMedia for channel logos
              channelLogo
            )}
          </>
        )}
        <DtvText content={label} variant="subhead1medium" sx={{
 fontSize: { sm: "30px !important" },
...labelStyles
}} />
      </DtvBox>
    );
  } catch (error) {
    console.error("Error in ModalChannelHeader >> ", error);
    return <ErrorDisplay component="ModalChannelHeader" error={error} />;
  }
};

export default React.memo(ModalChannelHeader);
// Generated by Copilot- End
